class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
    this.addToCartModule = null;
    this.footerElement = null;
    this.stickyTrigger = null;
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.addToCartForm = null;
    this.submitButton = this.querySelector("[data-sticky-order-btn]");
    this.qtyInput = this.querySelector("[data-sticky-qty-input]");
    this.qtyIncreaseBtn = this.querySelector("[data-sticky-qty-increase]");
    this.qtyDecreaseBtn = this.querySelector("[data-sticky-qty-decrease]");
    this.mainQtyInput = null;
    this.variantChangeUnsubscriber = null;
    this.intersectionObserver = null;
    this.isInitialized = false;
    this.cart = document.querySelector("cart-drawer");
  }

  connectedCallback() {
    if (!document.body.classList.contains("template-product")) return;

    if (!this.dataset.stickyMountedToBody && this.parentElement !== document.body) {
      this.dataset.stickyMountedToBody = "true";
      document.body.appendChild(this);
      return;
    }

    if (this.isInitialized) return;
    this.isInitialized = this.initialize();
  }

  disconnectedCallback() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.scrollHandler = null;
    this.variantChangeUnsubscriber = null;
    this.intersectionObserver = null;
    this.isInitialized = false;
  }

  initialize() {
    this.addToCartModule = document.querySelector(".wt-product__add-to-cart");
    this.addToCartForm = document.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!this.addToCartForm || !this.submitButton) return false;

    this.footerElement = document.querySelector(".wt-footer");
    this.stickyTrigger = document.querySelector(
      ".st-fbt[data-st-fbt-block-id], .st-fbt, .fbt-container, .wt-product__description, .wt-product__accordion",
    );

    this.mainQtyInput = this.addToCartForm.querySelector('input[name="quantity"]');

    this.bindQuantityControls();
    this.syncQuantityFromMain();

    // Add loading spinner to button if not present
    if (!this.submitButton.querySelector('.loading-overlay__spinner')) {
      const loadingSpinner = document.createElement('div');
      loadingSpinner.className = 'loading-overlay__spinner hidden';
      loadingSpinner.innerHTML = '<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';
      this.submitButton.appendChild(loadingSpinner);
    }

    this.submitButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.syncQuantityToMain();
      // Find the main add to cart button and click it instead
      const mainAddToCartButton = this.addToCartForm.querySelector('[type="submit"]');
      if (mainAddToCartButton) {
        // Show loading state on our button
        this.showLoadingState();
        // Trigger click on the main button
        mainAddToCartButton.click();
        // Hide loading state after a delay
        setTimeout(() => {
          this.disableLoadingState();
        }, 1200);
      }
    });

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (!variant) return;
        this.updateStickyPrices(variant);
        if (variant.available === false) {
          this.submitButton.setAttribute("disabled", "disabled");
          this.submitButton.querySelector("span").textContent = "Sold Out";
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButton.querySelector("span").textContent = "Order Now";
        }
      });
    }

    // Add scroll and resize listeners to control sticky visibility.
    this.scrollHandler = this.handleScroll.bind(this);
    this.resizeHandler = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler);

    // Check visibility on initial load and after layout settles.
    this.checkAddToCartVisibility(this.addToCartModule);
    setTimeout(() => {
      this.checkAddToCartVisibility(this.addToCartModule);
    }, 300);

    return true;
  }
  
  handleScroll() {
    this.checkAddToCartVisibility(this.addToCartModule);
  }
  
  checkAddToCartVisibility(addToCartModule) {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    let isAddToCartVisible = false;
    if (addToCartModule) {
      const rect = addToCartModule.getBoundingClientRect();
      isAddToCartVisible =
        rect.top < viewportHeight &&
        rect.bottom > 0 &&
        rect.left < viewportWidth &&
        rect.right > 0;
    }

    let isAfterTrigger = false;
    if (this.stickyTrigger) {
      const triggerRect = this.stickyTrigger.getBoundingClientRect();
      isAfterTrigger = triggerRect.bottom <= 0;
    } else {
      isAfterTrigger = window.scrollY > 420;
    }

    let isNearFooter = false;
    if (this.footerElement) {
      const footerRect = this.footerElement.getBoundingClientRect();
      isNearFooter = footerRect.top < (viewportHeight - 80);
    }

    if (isAddToCartVisible || !isAfterTrigger || isNearFooter) {
      this.classList.remove(this.activeClass);
    } else {
      this.classList.add(this.activeClass);
    }
  }

  bindQuantityControls() {
    if (!this.qtyInput || !this.qtyIncreaseBtn || !this.qtyDecreaseBtn) return;

    this.qtyIncreaseBtn.addEventListener("click", () => {
      const next = this.sanitizeQuantity(this.qtyInput.value) + 1;
      this.qtyInput.value = Math.min(next, 999);
      this.syncQuantityToMain();
    });

    this.qtyDecreaseBtn.addEventListener("click", () => {
      const next = this.sanitizeQuantity(this.qtyInput.value) - 1;
      this.qtyInput.value = Math.max(next, 1);
      this.syncQuantityToMain();
    });

    this.qtyInput.addEventListener("input", () => {
      this.qtyInput.value = String(this.sanitizeQuantity(this.qtyInput.value));
    });

    this.qtyInput.addEventListener("change", () => {
      this.qtyInput.value = String(this.sanitizeQuantity(this.qtyInput.value));
      this.syncQuantityToMain();
    });
  }

  sanitizeQuantity(value) {
    const parsed = parseInt(String(value).replace(/\D/g, ""), 10);
    if (Number.isNaN(parsed) || parsed < 1) return 1;
    if (parsed > 999) return 999;
    return parsed;
  }

  syncQuantityFromMain() {
    if (!this.qtyInput || !this.addToCartForm) return;
    const mainQuantity = this.addToCartForm.querySelector('input[name="quantity"]');
    if (mainQuantity) {
      this.mainQtyInput = mainQuantity;
      this.qtyInput.value = String(this.sanitizeQuantity(mainQuantity.value));
    } else {
      this.qtyInput.value = "1";
    }
  }

  syncQuantityToMain() {
    if (!this.qtyInput || !this.addToCartForm) return;
    const quantity = String(this.sanitizeQuantity(this.qtyInput.value));
    this.qtyInput.value = quantity;

    let mainQuantity = this.addToCartForm.querySelector('input[name="quantity"]');
    if (!mainQuantity) {
      mainQuantity = document.createElement("input");
      mainQuantity.type = "hidden";
      mainQuantity.name = "quantity";
      this.addToCartForm.appendChild(mainQuantity);
    }

    mainQuantity.value = quantity;
    mainQuantity.dispatchEvent(new Event("change", { bubbles: true }));
    this.mainQtyInput = mainQuantity;
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || "INR";
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format((cents || 0) / 100);
    } catch (err) {
      return `₹${Math.round((cents || 0) / 100).toLocaleString("en-IN")}`;
    }
  }

  updateStickyPrices(variant) {
    const currentPriceEl = this.querySelector("[data-sticky-current-price]");
    const comparePriceEl = this.querySelector("[data-sticky-compare-price]");
    const saveEl = this.querySelector("[data-sticky-save]");

    if (!currentPriceEl || !comparePriceEl || !saveEl) return;

    currentPriceEl.textContent = this.formatMoney(variant.price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const discount = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
      comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
      saveEl.textContent = `${discount}% Off`;
      comparePriceEl.classList.remove("hidden");
      saveEl.classList.remove("hidden");
    } else {
      comparePriceEl.textContent = "";
      saveEl.textContent = "";
      comparePriceEl.classList.add("hidden");
      saveEl.classList.add("hidden");
    }
  }

  showLoadingState() {
    this.submitButton.setAttribute("aria-disabled", true);
    this.submitButton.classList.add("loading");
    const loader = this.submitButton.querySelector(".loading-overlay__spinner");
    if (loader) loader.classList.remove("hidden");
  }
  
  disableLoadingState() {
    this.submitButton.removeAttribute("aria-disabled");
    this.submitButton.classList.remove("loading");
    const loader = this.submitButton.querySelector(".loading-overlay__spinner");
    if (loader) loader.classList.add("hidden");
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.className === "wt-product__img") {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll("img"),
            );
            const index = thumbnails.indexOf(event.target);

            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
